import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Box,
  LinearProgress,
} from '@mui/material';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import FirmwareUpdateSection from '../FirmwareUpdateSection';

// ---------------------------------------------------------------------------

const PHASE_LABELS = {
  starting: 'Starting update...',
  uploading: 'Uploading firmware...',
  verifying: 'Waiting for device to finish writing to flash...',
  committing: 'Committing update...',
  rebooting: 'Device is rebooting...',
};

export default function FirmwareTab({
  serialClient,
  deviceInfo,
  onFlash,
  connected,
  onError,
  latestFirmwareVersion,
}) {
  const [quickFlashing, setQuickFlashing] = useState(false);
  const [phase, setPhase] = useState(null);
  const [progress, setProgress] = useState(0);

  const hasUpdate =
    latestFirmwareVersion &&
    deviceInfo?.firmware &&
    latestFirmwareVersion !== deviceInfo.firmware;

  const handleQuickUpdate = useCallback(async () => {
    if (!latestFirmwareVersion || !onFlash) return;
    setQuickFlashing(true);
    setPhase('starting');
    setProgress(0);

    try {
      await onFlash(latestFirmwareVersion, (ph, received, total) => {
        setPhase(ph);
        setProgress(total > 0 ? Math.round((received / total) * 100) : 0);
      });
    } catch (err) {
      onError(err.message || 'Firmware update failed');
    } finally {
      setQuickFlashing(false);
      setPhase(null);
    }
  }, [latestFirmwareVersion, onFlash, onError]);

  return (
    <>
      <Typography variant="h6" gutterBottom>
        Firmware Update
      </Typography>

      {hasUpdate && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              A new firmware version is available.
            </Alert>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Current: <strong>v{deviceInfo.firmware}</strong> &rarr; Latest:{' '}
              <strong>v{latestFirmwareVersion}</strong>
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<SystemUpdateAltIcon />}
              onClick={handleQuickUpdate}
              disabled={quickFlashing || !connected}
            >
              {quickFlashing ? 'Updating...' : `Update to v${latestFirmwareVersion}`}
            </Button>

            {quickFlashing && phase && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  {PHASE_LABELS[phase] || phase}
                </Typography>
                <LinearProgress
                  variant={phase === 'uploading' ? 'determinate' : 'indeterminate'}
                  value={progress}
                />
                {phase === 'uploading' && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    {progress}%
                  </Typography>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {!hasUpdate && deviceInfo?.firmware && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Your sensor is running the latest firmware (v{deviceInfo.firmware}).
        </Alert>
      )}

      <FirmwareUpdateSection
        currentFirmware={deviceInfo?.firmware}
        onFlash={onFlash}
        disabled={!connected}
      />
    </>
  );
}

FirmwareTab.propTypes = {
  serialClient: PropTypes.object,
  deviceInfo: PropTypes.object,
  onFlash: PropTypes.func.isRequired,
  connected: PropTypes.bool.isRequired,
  onError: PropTypes.func.isRequired,
  latestFirmwareVersion: PropTypes.string,
};
